import { Box, FlexBox, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { Message } from 'lib-shared-legacy-types';
import { useCallback, useMemo, useState } from 'react';

import TextMessage, { TextMessageProps } from '../TextMessage';
import { getAttachment, getAttachmentType } from '../utils/attachment';
import { useTranslatePlainTextConversationMessage } from '../utils/useTranslatePlainTextConversationMessage';

import { StoryExpired } from './components/storyExpired';
import { StoryImage } from './components/storyImage';
import { StoryVideo } from './components/storyVideo';
import styles from './messageInstagram.module.scss';

type MessageInstagramProps = {
  conversationAttributes: Record<string, string>;
  message: Message;
} & TextMessageProps;

export const MessageInstagram = ({
  conversationAttributes: {
    instagram_account_name,
    instagram_account_username,
  },
  message,
  markup,
  ...otherProps
}: MessageInstagramProps): JSX.Element => {
  const [storyExpired, setStoryExpired] = useState(false);

  const handleStoryError = useCallback(() => {
    setStoryExpired(true);
  }, []);

  const storyAttachment = useMemo(() => {
    if (message.attached_files) {
      const attachment =
        message.attached_files instanceof Array
          ? message.attached_files[0]
          : message.attached_files;
      const type = getAttachmentType(attachment);

      if (type === 'image') {
        return (
          <StoryImage attachment={attachment} onError={handleStoryError} />
        );
      }

      if (type === 'video') {
        return (
          <StoryVideo
            attachment={attachment}
            type={type}
            onError={handleStoryError}
          />
        );
      }
    }

    return null;
  }, [handleStoryError, message]);

  const storyContent = useMemo(() => {
    return (
      <FlexBox
        flexDirection="column"
        margin="300 0 0 0"
        borderWidth="100"
        maxWidth="400px"
        borderColor={colors.neutral['200']}
      >
        <Box padding="300">
          <Text.Heading variant="heading-4">
            {instagram_account_name}
          </Text.Heading>
        </Box>
        <Box className={styles.storyAttachment}>
          {storyExpired ? <StoryExpired /> : storyAttachment}
        </Box>
      </FlexBox>
    );
  }, [instagram_account_name, storyAttachment, storyExpired]);

  const storyDescription = useMemo(() => {
    return (
      <Box margin="0 0 0 200">
        <Text.Paragraph variant="4" subdued>
          {message.text
            ? 'commented on your story'
            : `@${instagram_account_username} was mentioned in a story`}
        </Text.Paragraph>
      </Box>
    );
  }, [instagram_account_username, message.text]);

  const attachment = getAttachment(message, { scaleImage: true });

  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: message.text ?? undefined,
    isDisabled: !!attachment,
    originalLanguageCode: message.language ?? null,
    contentType: message.content_type === 'Html' ? 'html' : 'text',
  });

  return message.is_story ? (
    <TextMessage
      {...otherProps}
      message={message}
      content={storyContent}
      authorDescription={storyDescription}
      showMarkupAndContent
      translation={translation}
      gutterButtons={button}
    />
  ) : (
    <TextMessage
      {...otherProps}
      message={message}
      content={attachment}
      translation={translation}
      gutterButtons={button}
    />
  );
};
